/**
 * Centralized form options for consistent use across all modules
 * This file contains all the select options used in forms throughout the application
 */

// Language options for internationalization
/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: formOptions.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Centralized form options and data for consistent form components
    across the application, including languages, timezones, and more.

 🔧 Notes:
    - Centralizes all dropdown options for consistency
    - Includes comprehensive language, timezone, and country code options
    - Supports both interface and voice language selections
    - Optimized for performance with structured data
    - Reduces code duplication across components

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

// Default language and voice constants (used throughout the application)
export const DEFAULT_LANGUAGE = 'en-us-emma';
export const DEFAULT_LANGUAGE_CODE = 'en-us-emma';
export const DEFAULT_VOICE = 'emma';
export const DEFAULT_TIMEZONE = 'America/New_York';

// Voice guides available in the system (matches voice_guides table)
export const languageOptions = [
  { value: 'en-us-emma', label: 'English (US) - Emma' },
  { value: 'es-us-paloma', label: 'Spanish (US) - Paloma' }
];

// UI Languages available for the platform interface
export const uiLanguageOptions = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' }
];

// Comprehensive timezone options with GMT offsets
export const timezoneOptions = [
  // UTC
  { value: 'UTC', label: 'UTC - Coordinated Universal Time (GMT+0)' },

  // North America - United States
  { value: 'America/New_York', label: 'New York, Miami, Washington DC (GMT-5)' },
  { value: 'America/Chicago', label: 'Chicago, Dallas, Houston (GMT-6)' },
  { value: 'America/Denver', label: 'Denver, Salt Lake City (GMT-7)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles, San Francisco, Seattle (GMT-8)' },
  { value: 'America/Phoenix', label: 'Phoenix, Arizona (GMT-7)' },
  { value: 'America/Anchorage', label: 'Anchorage, Alaska (GMT-9)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu, Hawaii (GMT-10)' },

  // North America - Canada
  { value: 'America/Toronto', label: 'Toronto, Montreal, Ottawa (GMT-5)' },
  { value: 'America/Vancouver', label: 'Vancouver, Victoria (GMT-8)' },
  { value: 'America/Edmonton', label: 'Edmonton, Calgary (GMT-7)' },
  { value: 'America/Winnipeg', label: 'Winnipeg, Manitoba (GMT-6)' },
  { value: 'America/Halifax', label: 'Halifax, Nova Scotia (GMT-4)' },
  { value: 'America/St_Johns', label: 'St. Johns, Newfoundland (GMT-3:30)' },

  // Mexico
  { value: 'America/Mexico_City', label: 'Ciudad de México, Guadalajara, Monterrey (GMT-6)' },
  { value: 'America/Tijuana', label: 'Tijuana, Baja California (GMT-8)' },
  { value: 'America/Cancun', label: 'Cancún, Quintana Roo (GMT-5)' },
  { value: 'America/Merida', label: 'Mérida, Yucatán (GMT-6)' },

  // Central America
  { value: 'America/Guatemala', label: 'Guatemala City, Guatemala (GMT-6)' },
  { value: 'America/El_Salvador', label: 'San Salvador, El Salvador (GMT-6)' },
  { value: 'America/Tegucigalpa', label: 'Tegucigalpa, Honduras (GMT-6)' },
  { value: 'America/Managua', label: 'Managua, Nicaragua (GMT-6)' },
  { value: 'America/Costa_Rica', label: 'San José, Costa Rica (GMT-6)' },
  { value: 'America/Panama', label: 'Ciudad de Panamá, Panamá (GMT-5)' },
  { value: 'America/Belize', label: 'Belize City, Belize (GMT-6)' },

  // Caribbean
  { value: 'America/Havana', label: 'La Habana, Cuba (GMT-5)' },
  { value: 'America/Jamaica', label: 'Kingston, Jamaica (GMT-5)' },
  { value: 'America/Santo_Domingo', label: 'Santo Domingo, Dominican Republic (GMT-4)' },
  { value: 'America/Puerto_Rico', label: 'San Juan, Puerto Rico (GMT-4)' },
  { value: 'America/Port-au-Prince', label: 'Port-au-Prince, Haiti (GMT-5)' },
  { value: 'America/Barbados', label: 'Bridgetown, Barbados (GMT-4)' },
  { value: 'America/Martinique', label: 'Fort-de-France, Martinique (GMT-4)' },
  { value: 'America/Trinidad', label: 'Port of Spain, Trinidad (GMT-4)' },
  { value: 'America/Curacao', label: 'Willemstad, Curaçao (GMT-4)' },

  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo, Rio de Janeiro, Brasil (GMT-3)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires, Argentina (GMT-3)' },
  { value: 'America/Lima', label: 'Lima, Perú (GMT-5)' },
  { value: 'America/Bogota', label: 'Bogotá, Medellín, Colombia (GMT-5)' },
  { value: 'America/Caracas', label: 'Caracas, Venezuela (GMT-4)' },
  { value: 'America/Santiago', label: 'Santiago, Chile (GMT-4)' },
  { value: 'America/Quito', label: 'Quito, Guayaquil, Ecuador (GMT-5)' },
  { value: 'America/La_Paz', label: 'La Paz, Bolivia (GMT-4)' },
  { value: 'America/Asuncion', label: 'Asunción, Paraguay (GMT-4)' },
  { value: 'America/Montevideo', label: 'Montevideo, Uruguay (GMT-3)' },
  { value: 'America/Guyana', label: 'Georgetown, Guyana (GMT-4)' },
  { value: 'America/Paramaribo', label: 'Paramaribo, Suriname (GMT-3)' },
  { value: 'America/Cayenne', label: 'Cayenne, French Guiana (GMT-3)' },

  // Europe - Western
  { value: 'Europe/London', label: 'London, Edinburgh, UK (GMT+0)' },
  { value: 'Europe/Dublin', label: 'Dublin, Ireland (GMT+0)' },
  { value: 'Europe/Lisbon', label: 'Lisbon, Porto, Portugal (GMT+0)' },
  { value: 'Atlantic/Reykjavik', label: 'Reykjavik, Iceland (GMT+0)' },

  // Europe - Central
  { value: 'Europe/Paris', label: 'Paris, Lyon, France (GMT+1)' },
  { value: 'Europe/Berlin', label: 'Berlin, Munich, Frankfurt, Germany (GMT+1)' },
  { value: 'Europe/Madrid', label: 'Madrid, Barcelona, Spain (GMT+1)' },
  { value: 'Europe/Rome', label: 'Rome, Milan, Naples, Italy (GMT+1)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam, Rotterdam, Netherlands (GMT+1)' },
  { value: 'Europe/Brussels', label: 'Brussels, Antwerp, Belgium (GMT+1)' },
  { value: 'Europe/Vienna', label: 'Vienna, Salzburg, Austria (GMT+1)' },
  { value: 'Europe/Zurich', label: 'Zurich, Geneva, Switzerland (GMT+1)' },
  { value: 'Europe/Stockholm', label: 'Stockholm, Gothenburg, Sweden (GMT+1)' },
  { value: 'Europe/Oslo', label: 'Oslo, Bergen, Norway (GMT+1)' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen, Aarhus, Denmark (GMT+1)' },
  { value: 'Europe/Warsaw', label: 'Warsaw, Krakow, Poland (GMT+1)' },
  { value: 'Europe/Prague', label: 'Prague, Brno, Czech Republic (GMT+1)' },
  { value: 'Europe/Budapest', label: 'Budapest, Hungary (GMT+1)' },
  { value: 'Europe/Luxembourg', label: 'Luxembourg City, Luxembourg (GMT+1)' },

  // Europe - Eastern
  { value: 'Europe/Helsinki', label: 'Helsinki, Espoo, Finland (GMT+2)' },
  { value: 'Europe/Athens', label: 'Athens, Thessaloniki, Greece (GMT+2)' },
  { value: 'Europe/Bucharest', label: 'Bucharest, Cluj-Napoca, Romania (GMT+2)' },
  { value: 'Europe/Sofia', label: 'Sofia, Plovdiv, Bulgaria (GMT+2)' },
  { value: 'Europe/Kiev', label: 'Kyiv, Kharkiv, Ukraine (GMT+2)' },
  { value: 'Europe/Riga', label: 'Riga, Latvia (GMT+2)' },
  { value: 'Europe/Vilnius', label: 'Vilnius, Lithuania (GMT+2)' },
  { value: 'Europe/Tallinn', label: 'Tallinn, Estonia (GMT+2)' },
  { value: 'Europe/Istanbul', label: 'Istanbul, Ankara, Turkey (GMT+3)' },
  { value: 'Europe/Moscow', label: 'Moscow, St. Petersburg, Russia (GMT+3)' },
  { value: 'Europe/Minsk', label: 'Minsk, Belarus (GMT+3)' },

  // Middle East
  { value: 'Asia/Dubai', label: 'Dubai, Abu Dhabi, UAE (GMT+4)' },
  { value: 'Asia/Riyadh', label: 'Riyadh, Jeddah, Saudi Arabia (GMT+3)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem, Tel Aviv, Israel (GMT+2)' },
  { value: 'Asia/Beirut', label: 'Beirut, Lebanon (GMT+2)' },
  { value: 'Asia/Amman', label: 'Amman, Jordan (GMT+2)' },
  { value: 'Asia/Baghdad', label: 'Baghdad, Basra, Iraq (GMT+3)' },
  { value: 'Asia/Kuwait', label: 'Kuwait City, Kuwait (GMT+3)' },
  { value: 'Asia/Qatar', label: 'Doha, Qatar (GMT+3)' },
  { value: 'Asia/Bahrain', label: 'Manama, Bahrain (GMT+3)' },
  { value: 'Asia/Muscat', label: 'Muscat, Oman (GMT+4)' },
  { value: 'Asia/Tehran', label: 'Tehran, Mashhad, Iran (GMT+3:30)' },

  // South Asia
  { value: 'Asia/Kolkata', label: 'Mumbai, Delhi, Bangalore, India (GMT+5:30)' },
  { value: 'Asia/Karachi', label: 'Karachi, Lahore, Pakistan (GMT+5)' },
  { value: 'Asia/Dhaka', label: 'Dhaka, Chittagong, Bangladesh (GMT+6)' },
  { value: 'Asia/Colombo', label: 'Colombo, Sri Lanka (GMT+5:30)' },
  { value: 'Asia/Kathmandu', label: 'Kathmandu, Nepal (GMT+5:45)' },

  // Southeast Asia
  { value: 'Asia/Bangkok', label: 'Bangkok, Phuket, Thailand (GMT+7)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City, Hanoi, Vietnam (GMT+7)' },
  { value: 'Asia/Jakarta', label: 'Jakarta, Surabaya, Indonesia (GMT+7)' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur, Malaysia (GMT+8)' },
  { value: 'Asia/Manila', label: 'Manila, Cebu, Philippines (GMT+8)' },
  { value: 'Asia/Phnom_Penh', label: 'Phnom Penh, Cambodia (GMT+7)' },
  { value: 'Asia/Yangon', label: 'Yangon, Myanmar (GMT+6:30)' },

  // East Asia
  { value: 'Asia/Tokyo', label: 'Tokyo, Osaka, Yokohama, Japan (GMT+9)' },
  { value: 'Asia/Shanghai', label: 'Shanghai, Beijing, Shenzhen, China (GMT+8)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (GMT+8)' },
  { value: 'Asia/Seoul', label: 'Seoul, Busan, South Korea (GMT+9)' },
  { value: 'Asia/Taipei', label: 'Taipei, Kaohsiung, Taiwan (GMT+8)' },
  { value: 'Asia/Macau', label: 'Macau (GMT+8)' },
  { value: 'Asia/Ulaanbaatar', label: 'Ulaanbaatar, Mongolia (GMT+8)' },

  // Central Asia
  { value: 'Asia/Almaty', label: 'Almaty, Kazakhstan (GMT+6)' },
  { value: 'Asia/Tashkent', label: 'Tashkent, Uzbekistan (GMT+5)' },
  { value: 'Asia/Bishkek', label: 'Bishkek, Kyrgyzstan (GMT+6)' },
  { value: 'Asia/Baku', label: 'Baku, Azerbaijan (GMT+4)' },
  { value: 'Asia/Tbilisi', label: 'Tbilisi, Georgia (GMT+4)' },
  { value: 'Asia/Yerevan', label: 'Yerevan, Armenia (GMT+4)' },

  // Africa - North
  { value: 'Africa/Cairo', label: 'Cairo, Alexandria, Egypt (GMT+2)' },
  { value: 'Africa/Casablanca', label: 'Casablanca, Rabat, Morocco (GMT+0)' },
  { value: 'Africa/Tunis', label: 'Tunis, Tunisia (GMT+1)' },
  { value: 'Africa/Algiers', label: 'Algiers, Oran, Algeria (GMT+1)' },
  { value: 'Africa/Tripoli', label: 'Tripoli, Libya (GMT+2)' },

  // Africa - West
  { value: 'Africa/Lagos', label: 'Lagos, Abuja, Nigeria (GMT+1)' },
  { value: 'Africa/Accra', label: 'Accra, Kumasi, Ghana (GMT+0)' },
  { value: 'Africa/Dakar', label: 'Dakar, Senegal (GMT+0)' },
  { value: 'Africa/Abidjan', label: 'Abidjan, Ivory Coast (GMT+0)' },

  // Africa - East
  { value: 'Africa/Nairobi', label: 'Nairobi, Mombasa, Kenya (GMT+3)' },
  { value: 'Africa/Addis_Ababa', label: 'Addis Ababa, Ethiopia (GMT+3)' },
  { value: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam, Tanzania (GMT+3)' },
  { value: 'Africa/Kampala', label: 'Kampala, Uganda (GMT+3)' },

  // Africa - South
  { value: 'Africa/Johannesburg', label: 'Johannesburg, Cape Town, South Africa (GMT+2)' },
  { value: 'Africa/Harare', label: 'Harare, Zimbabwe (GMT+2)' },
  { value: 'Africa/Lusaka', label: 'Lusaka, Zambia (GMT+2)' },

  // Australia
  { value: 'Australia/Sydney', label: 'Sydney, Canberra, Australia (GMT+10)' },
  { value: 'Australia/Melbourne', label: 'Melbourne, Victoria, Australia (GMT+10)' },
  { value: 'Australia/Brisbane', label: 'Brisbane, Gold Coast, Australia (GMT+10)' },
  { value: 'Australia/Perth', label: 'Perth, Western Australia (GMT+8)' },
  { value: 'Australia/Adelaide', label: 'Adelaide, South Australia (GMT+9:30)' },
  { value: 'Australia/Darwin', label: 'Darwin, Northern Territory (GMT+9:30)' },
  { value: 'Australia/Hobart', label: 'Hobart, Tasmania (GMT+10)' },

  // Oceania
  { value: 'Pacific/Auckland', label: 'Auckland, Wellington, New Zealand (GMT+12)' },
  { value: 'Pacific/Fiji', label: 'Suva, Fiji (GMT+12)' },
  { value: 'Pacific/Port_Moresby', label: 'Port Moresby, Papua New Guinea (GMT+10)' },
  { value: 'Pacific/Guam', label: 'Guam, Saipan (GMT+10)' },
  { value: 'Pacific/Tahiti', label: 'Papeete, Tahiti (GMT-10)' },
  { value: 'Pacific/Samoa', label: 'Apia, Samoa (GMT+13)' }
];

// Country code options for international dialing
export const countryCodeOptions = [
  { value: '1', label: '1 (US/Canada)' },
  { value: '7', label: '7 (Russia/Kazakhstan)' },
  { value: '20', label: '20 (Egypt)' },
  { value: '27', label: '27 (South Africa)' },
  { value: '30', label: '30 (Greece)' },
  { value: '31', label: '31 (Netherlands)' },
  { value: '32', label: '32 (Belgium)' },
  { value: '33', label: '33 (France)' },
  { value: '34', label: '34 (Spain)' },
  { value: '36', label: '36 (Hungary)' },
  { value: '39', label: '39 (Italy)' },
  { value: '40', label: '40 (Romania)' },
  { value: '41', label: '41 (Switzerland)' },
  { value: '43', label: '43 (Austria)' },
  { value: '44', label: '44 (UK)' },
  { value: '45', label: '45 (Denmark)' },
  { value: '46', label: '46 (Sweden)' },
  { value: '47', label: '47 (Norway)' },
  { value: '48', label: '48 (Poland)' },
  { value: '49', label: '49 (Germany)' },
  { value: '51', label: '51 (Peru)' },
  { value: '52', label: '52 (Mexico)' },
  { value: '53', label: '53 (Cuba)' },
  { value: '54', label: '54 (Argentina)' },
  { value: '55', label: '55 (Brazil)' },
  { value: '56', label: '56 (Chile)' },
  { value: '57', label: '57 (Colombia)' },
  { value: '58', label: '58 (Venezuela)' },
  { value: '60', label: '60 (Malaysia)' },
  { value: '61', label: '61 (Australia)' },
  { value: '62', label: '62 (Indonesia)' },
  { value: '63', label: '63 (Philippines)' },
  { value: '64', label: '64 (New Zealand)' },
  { value: '65', label: '65 (Singapore)' },
  { value: '66', label: '66 (Thailand)' },
  { value: '81', label: '81 (Japan)' },
  { value: '82', label: '82 (South Korea)' },
  { value: '84', label: '84 (Vietnam)' },
  { value: '86', label: '86 (China)' },
  { value: '90', label: '90 (Turkey)' },
  { value: '91', label: '91 (India)' },
  { value: '92', label: '92 (Pakistan)' },
  { value: '93', label: '93 (Afghanistan)' },
  { value: '94', label: '94 (Sri Lanka)' },
  { value: '95', label: '95 (Myanmar)' },
  { value: '98', label: '98 (Iran)' },
  { value: '212', label: '212 (Morocco)' },
  { value: '213', label: '213 (Algeria)' },
  { value: '216', label: '216 (Tunisia)' },
  { value: '218', label: '218 (Libya)' },
  { value: '220', label: '220 (Gambia)' },
  { value: '221', label: '221 (Senegal)' },
  { value: '222', label: '222 (Mauritania)' },
  { value: '223', label: '223 (Mali)' },
  { value: '224', label: '224 (Guinea)' },
  { value: '225', label: '225 (Ivory Coast)' },
  { value: '226', label: '226 (Burkina Faso)' },
  { value: '227', label: '227 (Niger)' },
  { value: '228', label: '228 (Togo)' },
  { value: '229', label: '229 (Benin)' },
  { value: '230', label: '230 (Mauritius)' },
  { value: '231', label: '231 (Liberia)' },
  { value: '232', label: '232 (Sierra Leone)' },
  { value: '233', label: '233 (Ghana)' },
  { value: '234', label: '234 (Nigeria)' },
  { value: '235', label: '235 (Chad)' },
  { value: '236', label: '236 (Central African Republic)' },
  { value: '237', label: '237 (Cameroon)' },
  { value: '238', label: '238 (Cape Verde)' },
  { value: '239', label: '239 (São Tomé and Príncipe)' },
  { value: '240', label: '240 (Equatorial Guinea)' },
  { value: '241', label: '241 (Gabon)' },
  { value: '242', label: '242 (Republic of the Congo)' },
  { value: '243', label: '243 (Democratic Republic of the Congo)' },
  { value: '244', label: '244 (Angola)' },
  { value: '245', label: '245 (Guinea-Bissau)' },
  { value: '246', label: '246 (British Indian Ocean Territory)' },
  { value: '248', label: '248 (Seychelles)' },
  { value: '249', label: '249 (Sudan)' },
  { value: '250', label: '250 (Rwanda)' },
  { value: '251', label: '251 (Ethiopia)' },
  { value: '252', label: '252 (Somalia)' },
  { value: '253', label: '253 (Djibouti)' },
  { value: '254', label: '254 (Kenya)' },
  { value: '255', label: '255 (Tanzania)' },
  { value: '256', label: '256 (Uganda)' },
  { value: '257', label: '257 (Burundi)' },
  { value: '258', label: '258 (Mozambique)' },
  { value: '260', label: '260 (Zambia)' },
  { value: '261', label: '261 (Madagascar)' },
  { value: '262', label: '262 (Réunion)' },
  { value: '263', label: '263 (Zimbabwe)' },
  { value: '264', label: '264 (Namibia)' },
  { value: '265', label: '265 (Malawi)' },
  { value: '266', label: '266 (Lesotho)' },
  { value: '267', label: '267 (Botswana)' },
  { value: '268', label: '268 (Swaziland)' },
  { value: '269', label: '269 (Comoros)' },
  { value: '290', label: '290 (Saint Helena)' },
  { value: '291', label: '291 (Eritrea)' },
  { value: '297', label: '297 (Aruba)' },
  { value: '298', label: '298 (Faroe Islands)' },
  { value: '299', label: '299 (Greenland)' },
  { value: '350', label: '350 (Gibraltar)' },
  { value: '351', label: '351 (Portugal)' },
  { value: '352', label: '352 (Luxembourg)' },
  { value: '353', label: '353 (Ireland)' },
  { value: '354', label: '354 (Iceland)' },
  { value: '355', label: '355 (Albania)' },
  { value: '356', label: '356 (Malta)' },
  { value: '357', label: '357 (Cyprus)' },
  { value: '358', label: '358 (Finland)' },
  { value: '359', label: '359 (Bulgaria)' },
  { value: '370', label: '370 (Lithuania)' },
  { value: '371', label: '371 (Latvia)' },
  { value: '372', label: '372 (Estonia)' },
  { value: '373', label: '373 (Moldova)' },
  { value: '374', label: '374 (Armenia)' },
  { value: '375', label: '375 (Belarus)' },
  { value: '376', label: '376 (Andorra)' },
  { value: '377', label: '377 (Monaco)' },
  { value: '378', label: '378 (San Marino)' },
  { value: '380', label: '380 (Ukraine)' },
  { value: '381', label: '381 (Serbia)' },
  { value: '382', label: '382 (Montenegro)' },
  { value: '383', label: '383 (Kosovo)' },
  { value: '385', label: '385 (Croatia)' },
  { value: '386', label: '386 (Slovenia)' },
  { value: '387', label: '387 (Bosnia and Herzegovina)' },
  { value: '389', label: '389 (North Macedonia)' },
  { value: '420', label: '420 (Czech Republic)' },
  { value: '421', label: '421 (Slovakia)' },
  { value: '423', label: '423 (Liechtenstein)' },
  { value: '500', label: '500 (Falkland Islands)' },
  { value: '501', label: '501 (Belize)' },
  { value: '502', label: '502 (Guatemala)' },
  { value: '503', label: '503 (El Salvador)' },
  { value: '504', label: '504 (Honduras)' },
  { value: '505', label: '505 (Nicaragua)' },
  { value: '506', label: '506 (Costa Rica)' },
  { value: '507', label: '507 (Panama)' },
  { value: '508', label: '508 (Saint Pierre and Miquelon)' },
  { value: '509', label: '509 (Haiti)' },
  { value: '590', label: '590 (Guadeloupe)' },
  { value: '591', label: '591 (Bolivia)' },
  { value: '592', label: '592 (Guyana)' },
  { value: '593', label: '593 (Ecuador)' },
  { value: '594', label: '594 (French Guiana)' },
  { value: '595', label: '595 (Paraguay)' },
  { value: '596', label: '596 (Martinique)' },
  { value: '597', label: '597 (Suriname)' },
  { value: '598', label: '598 (Uruguay)' },
  { value: '599', label: '599 (Netherlands Antilles)' },
  { value: '670', label: '670 (East Timor)' },
  { value: '672', label: '672 (Antarctica)' },
  { value: '673', label: '673 (Brunei)' },
  { value: '674', label: '674 (Nauru)' },
  { value: '675', label: '675 (Papua New Guinea)' },
  { value: '676', label: '676 (Tonga)' },
  { value: '677', label: '677 (Solomon Islands)' },
  { value: '678', label: '678 (Vanuatu)' },
  { value: '679', label: '679 (Fiji)' },
  { value: '680', label: '680 (Palau)' },
  { value: '681', label: '681 (Wallis and Futuna)' },
  { value: '682', label: '682 (Cook Islands)' },
  { value: '683', label: '683 (Niue)' },
  { value: '684', label: '684 (American Samoa)' },
  { value: '685', label: '685 (Samoa)' },
  { value: '686', label: '686 (Kiribati)' },
  { value: '687', label: '687 (New Caledonia)' },
  { value: '688', label: '688 (Tuvalu)' },
  { value: '689', label: '689 (French Polynesia)' },
  { value: '690', label: '690 (Tokelau)' },
  { value: '691', label: '691 (Micronesia)' },
  { value: '692', label: '692 (Marshall Islands)' },
  { value: '850', label: '850 (North Korea)' },
  { value: '852', label: '852 (Hong Kong)' },
  { value: '853', label: '853 (Macau)' },
  { value: '855', label: '855 (Cambodia)' },
  { value: '856', label: '856 (Laos)' },
  { value: '880', label: '880 (Bangladesh)' },
  { value: '886', label: '886 (Taiwan)' },
  { value: '960', label: '960 (Maldives)' },
  { value: '961', label: '961 (Lebanon)' },
  { value: '962', label: '962 (Jordan)' },
  { value: '963', label: '963 (Syria)' },
  { value: '964', label: '964 (Iraq)' },
  { value: '965', label: '965 (Kuwait)' },
  { value: '966', label: '966 (Saudi Arabia)' },
  { value: '967', label: '967 (Yemen)' },
  { value: '968', label: '968 (Oman)' },
  { value: '970', label: '970 (Palestine)' },
  { value: '971', label: '971 (UAE)' },
  { value: '972', label: '972 (Israel)' },
  { value: '973', label: '973 (Bahrain)' },
  { value: '974', label: '974 (Qatar)' },
  { value: '975', label: '975 (Bhutan)' },
  { value: '976', label: '976 (Mongolia)' },
  { value: '977', label: '977 (Nepal)' },
  { value: '992', label: '992 (Tajikistan)' },
  { value: '993', label: '993 (Turkmenistan)' },
  { value: '994', label: '994 (Azerbaijan)' },
  { value: '995', label: '995 (Georgia)' },
  { value: '996', label: '996 (Kyrgyzstan)' },
  { value: '998', label: '998 (Uzbekistan)' }
];

// Startup dialog options for user interface
export const startupDialogOptions = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'calls', label: 'Calls' },
  { value: 'users', label: 'Users' },
  { value: 'tenants', label: 'Tenants' },
  { value: 'domains', label: 'Domains' },
  { value: 'servers', label: 'Servers' },
  { value: 'settings', label: 'Settings' }
];

// Department options for user organization
export const departmentOptions = [
  { value: 'IT', label: 'IT' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Support', label: 'Support' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'HR', label: 'Human Resources' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Management', label: 'Management' },
  { value: 'Customer Service', label: 'Customer Service' },
  { value: 'Technical', label: 'Technical' },
  { value: 'Administration', label: 'Administration' },
  { value: 'Other', label: 'Other' }
];

// Authentication type options for email settings
export const authTypeOptions = [
  { value: 'none', label: 'None' },
  { value: 'login', label: 'Login' },
  { value: 'plain', label: 'Plain' },
  { value: 'cram-md5', label: 'CRAM-MD5' },
  { value: 'digest-md5', label: 'DIGEST-MD5' },
  { value: 'ntlm', label: 'NTLM' }
];

// SMTP port options
export const smtpPortOptions = [
  { value: '25', label: '25 (Standard SMTP)' },
  { value: '465', label: '465 (SMTP over SSL)' },
  { value: '587', label: '587 (SMTP with STARTTLS)' },
  { value: '2525', label: '2525 (Alternative SMTP)' },
  { value: '8025', label: '8025 (Alternative SMTP)' }
];

// Helper function to get timezone by region
export const getTimezonesByRegion = (region: 'americas' | 'europe' | 'asia' | 'africa' | 'oceania') => {
  const regionMap = {
    americas: timezoneOptions.filter(tz =>
      tz.value.includes('America/') || tz.value.includes('Pacific/Honolulu')
    ),
    europe: timezoneOptions.filter(tz =>
      tz.value.includes('Europe/') || tz.value === 'UTC'
    ),
    asia: timezoneOptions.filter(tz =>
      tz.value.includes('Asia/')
    ),
    africa: timezoneOptions.filter(tz =>
      tz.value.includes('Africa/')
    ),
    oceania: timezoneOptions.filter(tz =>
      tz.value.includes('Australia/') || tz.value.includes('Pacific/')
    )
  };

  return regionMap[region] || [];
};

// Helper function to get country codes by region
export const getCountryCodesByRegion = (region: 'americas' | 'europe' | 'asia' | 'africa' | 'oceania') => {
  const regionMap = {
    americas: countryCodeOptions.filter(cc =>
      ['1', '52', '53', '54', '55', '56', '57', '58', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599'].includes(cc.value)
    ),
    europe: countryCodeOptions.filter(cc =>
      ['7', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423'].includes(cc.value)
    ),
    asia: countryCodeOptions.filter(cc =>
      ['7', '60', '62', '63', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'].includes(cc.value)
    ),
    africa: countryCodeOptions.filter(cc =>
      ['20', '27', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291'].includes(cc.value)
    ),
    oceania: countryCodeOptions.filter(cc =>
      ['61', '64', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692'].includes(cc.value)
    )
  };

  return regionMap[region] || [];
};
